import * as React from 'react';
import { useSearchParams } from 'react-router-dom';
import { PAGINATION } from '@/constants/config';

interface UsePaginationOptions {
  defaultPage?: number;
  defaultLimit?: number;
  syncWithUrl?: boolean;
}

interface UsePaginationReturn {
  page: number;
  limit: number;
  offset: number;
  setPage: (page: number) => void;
  setLimit: (limit: number) => void;
  reset: () => void;
}

export function usePagination({
  defaultPage = PAGINATION.defaultPage,
  defaultLimit = PAGINATION.defaultLimit,
  syncWithUrl = true,
}: UsePaginationOptions = {}): UsePaginationReturn {
  const [searchParams, setSearchParams] = useSearchParams();

  const getInitialPage = () => {
    if (syncWithUrl) {
      const urlPage = searchParams.get('page');
      return urlPage ? parseInt(urlPage, 10) : defaultPage;
    }
    return defaultPage;
  };

  const getInitialLimit = () => {
    if (syncWithUrl) {
      const urlLimit = searchParams.get('limit');
      return urlLimit ? parseInt(urlLimit, 10) : defaultLimit;
    }
    return defaultLimit;
  };

  const [page, setPageState] = React.useState(getInitialPage);
  const [limit, setLimitState] = React.useState(getInitialLimit);

  const setPage = React.useCallback(
    (newPage: number) => {
      setPageState(newPage);
      if (syncWithUrl) {
        setSearchParams((prev) => {
          prev.set('page', String(newPage));
          return prev;
        });
      }
    },
    [syncWithUrl, setSearchParams]
  );

  const setLimit = React.useCallback(
    (newLimit: number) => {
      setLimitState(newLimit);
      setPageState(1); // Reset to first page when limit changes
      if (syncWithUrl) {
        setSearchParams((prev) => {
          prev.set('limit', String(newLimit));
          prev.set('page', '1');
          return prev;
        });
      }
    },
    [syncWithUrl, setSearchParams]
  );

  const reset = React.useCallback(() => {
    setPageState(defaultPage);
    setLimitState(defaultLimit);
    if (syncWithUrl) {
      setSearchParams((prev) => {
        prev.delete('page');
        prev.delete('limit');
        return prev;
      });
    }
  }, [defaultPage, defaultLimit, syncWithUrl, setSearchParams]);

  const offset = (page - 1) * limit;

  return {
    page,
    limit,
    offset,
    setPage,
    setLimit,
    reset,
  };
}
